import { motion } from "framer-motion";
import { useTheme } from "../contexts/ThemeContext";

// Futuristic HUD Viewfinder Overlay Component
// Clean, minimal design with optional elements
const ViewfinderOverlay = ({ 
  isRecording, 
  isScanning, 
  isLocked,
  showReticle = false,  // Optional crosshairs - off by default
  showGrid = false      // Optional grid - off by default
}) => {
  const { theme } = useTheme();
  const primaryColor = theme?.colors?.primary || "#00f2ea";
  
  return (
    <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
      {/* Subtle Vignette Effect */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at center, transparent 40%, rgba(2,4,10,0.5) 100%)'
        }}
      />
      
      {/* Very Faint Moving Scan Line */}
      <motion.div
        className="absolute left-0 right-0 h-[1px]"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${primaryColor}15 30%, ${primaryColor}25 50%, ${primaryColor}15 70%, transparent 100%)`,
          boxShadow: `0 0 8px ${primaryColor}10`
        }}
        initial={{ top: '0%' }}
        animate={{ top: ['0%', '100%', '0%'] }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'linear'
        }}
      />
      
      {/* Corner Brackets - Top Left */}
      <svg className="absolute top-3 left-3 w-10 h-10" viewBox="0 0 40 40">
        <motion.path
          d="M2 14 L2 2 L14 2"
          fill="none"
          stroke={primaryColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeOpacity={isLocked ? 0.8 : 0.4}
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.5 }}
        />
      </svg>
      
      {/* Corner Brackets - Top Right */}
      <svg className="absolute top-3 right-3 w-10 h-10" viewBox="0 0 40 40">
        <motion.path
          d="M26 2 L38 2 L38 14"
          fill="none"
          stroke={primaryColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeOpacity={isLocked ? 0.8 : 0.4}
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        />
      </svg>
      
      {/* Corner Brackets - Bottom Left */}
      <svg className="absolute bottom-3 left-3 w-10 h-10" viewBox="0 0 40 40">
        <motion.path
          d="M2 26 L2 38 L14 38"
          fill="none"
          stroke={primaryColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeOpacity={isLocked ? 0.8 : 0.4}
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        />
      </svg>
      
      {/* Corner Brackets - Bottom Right */}
      <svg className="absolute bottom-3 right-3 w-10 h-10" viewBox="0 0 40 40">
        <motion.path
          d="M38 26 L38 38 L26 38"
          fill="none"
          stroke={primaryColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeOpacity={isLocked ? 0.8 : 0.4}
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        />
      </svg>
      
      {/* Optional Center Reticle - Only shown when enabled */}
      {showReticle && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          {/* Outer Ring */}
          <motion.div
            className="w-16 h-16 rounded-full border border-dashed"
            style={{ borderColor: `${primaryColor}20` }}
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
          />
          
          {/* Cross Hairs */}
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-[1px]"
            style={{ backgroundColor: `${primaryColor}30` }}
          />
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-4 w-[1px]"
            style={{ backgroundColor: `${primaryColor}30` }}
          />
        </div>
      )}
      
      {/* Optional Grid Overlay - Only shown when enabled */}
      {showGrid && (
        <div 
          className="absolute inset-8 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(${primaryColor} 1px, transparent 1px), linear-gradient(90deg, ${primaryColor} 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
      )}
      
      {/* Status Text - Bottom Center */}
      <motion.div
        className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-3 px-3 py-1.5 rounded-full backdrop-blur-sm"
        style={{
          background: 'rgba(2,4,10,0.5)',
          border: `1px solid ${primaryColor}15`
        }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <motion.div
          className="w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: isRecording ? '#ff2e2e' : primaryColor }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        <span 
          className="font-mono text-[9px] uppercase tracking-widest"
          style={{ color: `${primaryColor}90` }}
        >
          {isRecording ? 'REC' : isScanning ? 'SCANNING' : 'SKYBOT'}
        </span>
      </motion.div>
    </div>
  );
};

export default ViewfinderOverlay;
